import styled from "styled-components";
import { Box, Flex } from "rebass";
import { FaChevronDown } from "react-icons/fa";
import { useRecoilState, useRecoilValue, useSetRecoilState } from "recoil";
import { useEffect } from "react";
import { useHistory, useLocation } from "react-router-dom";

import { Tooltip } from "MVVM/Components/Tooltip";
import {
  RoundedRectangularButton,
  RoundedButton,
} from "MVVM/Components/Button/WithActiveState";
import { ESupportedGames } from "MVVM/@types";
import { GAME_TO_ICON_MAP } from "MVVM/Components/GameSelector";
import { MatchHistoryGame } from "MVVM/State/matchHistory";
import { UserGamePreference } from "MVVM/State/user";
import { useLinkedAccounts } from "MVVM/Hooks/useLinkedAccounts";

const GameSelector = () => {
  const [matchHistoryGame, setMatchHistoryGame] =
    useRecoilState(MatchHistoryGame);
  const { hasSteamLinked, hasRiotLinked, hasEpicLinked } = useLinkedAccounts();
  const setUserGamePreference = useSetRecoilState(UserGamePreference);
  const history = useHistory();
  const location = useLocation();

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const hasGameParam = params.get("game");

    if (matchHistoryGame === ESupportedGames.NONE && !hasGameParam) {
      if (hasSteamLinked) {
        setMatchHistoryGame(ESupportedGames.CS2);
      } else if (hasRiotLinked) {
        setMatchHistoryGame(ESupportedGames.LOL);
      } else if (hasEpicLinked) {
        setMatchHistoryGame(ESupportedGames.FORTNITE);
      }
    }
  }, [
    matchHistoryGame,
    hasRiotLinked,
    hasEpicLinked,
    hasSteamLinked,
    setMatchHistoryGame,
  ]);

  const onGameChange = (game: ESupportedGames) => {
    setMatchHistoryGame(game);
    setUserGamePreference(game);

    const params = new URLSearchParams(location.search);
    params.delete("game");
    params.delete("matchId");

    const newSearch = params.toString();
    const newUrl = `${location.pathname}${newSearch ? `?${newSearch}` : ""}`;

    if (newUrl !== `${location.pathname}${location.search}`) {
      history.replace(newUrl);
    }
  };

  return (
    <StyledToolTip
      tooltipContent={<TooltipContent onGameChange={onGameChange} />}
    >
      <RoundedButton>
        {GAME_TO_ICON_MAP[matchHistoryGame]}
        <FaChevronDown style={{ marginLeft: 5, flexShrink: 0 }} />
      </RoundedButton>
    </StyledToolTip>
  );
};

const TooltipContent = ({
  onGameChange,
}: {
  onGameChange: (game: ESupportedGames) => void;
}) => {
  const matchHistoryGame = useRecoilValue(MatchHistoryGame);

  const handleClick = (game: ESupportedGames) => {
    onGameChange(game);
  };

  return (
    <Flex
      flexDirection={"column"}
      style={{ gap: 5, width: "135px", alignSelf: "start" }}
    >
      <RoundedRectangularButton
        mr={2}
        active={matchHistoryGame === ESupportedGames.CS2}
        onClick={() => handleClick(ESupportedGames.CS2)}
      >
        {GAME_TO_ICON_MAP[ESupportedGames.CS2]}
        <Box>CS2</Box>
      </RoundedRectangularButton>
      <RoundedRectangularButton
        active={matchHistoryGame === ESupportedGames.LOL}
        onClick={() => handleClick(ESupportedGames.LOL)}
      >
        {GAME_TO_ICON_MAP[ESupportedGames.LOL]}
        <Box>LoL</Box>
      </RoundedRectangularButton>
      <RoundedRectangularButton
        active={matchHistoryGame === ESupportedGames.FORTNITE}
        onClick={() => handleClick(ESupportedGames.FORTNITE)}
      >
        {GAME_TO_ICON_MAP[ESupportedGames.FORTNITE]}
        <Box>Fortnite</Box>
      </RoundedRectangularButton>
      <RoundedRectangularButton
        active={matchHistoryGame === ESupportedGames.DOTA2}
        onClick={() => handleClick(ESupportedGames.DOTA2)}
      >
        {GAME_TO_ICON_MAP[ESupportedGames.DOTA2]}
        <Box>Dota 2</Box>
      </RoundedRectangularButton>
    </Flex>
  );
};

const StyledToolTip = styled(Tooltip)`
  &:hover {
    cursor: pointer;
  }
`;

export { GameSelector };
